import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
    ArrowLeft,
    Download,
    ExternalLink,
    ZoomIn,
    ZoomOut,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ResourceViewer = ({ resource, onBack }) => {
    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [scale, setScale] = useState(1.0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get the actual URL for the resource
    const resourceUrl = resource?.url || resource?.fileUrl || resource?.resourceUrl;

    // Set loading to false if no URL is available
    React.useEffect(() => {
        if (!resourceUrl) {
            setLoading(false);
            setError('No file URL available');
        }
    }, [resourceUrl]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setLoading(false);
        setError(null);
    };

    const onDocumentLoadError = (error) => {
        setError('Failed to load document');
        setLoading(false);
        console.error('Error loading document:', error);
    };

    const handleDownloadFile = () => {
        if (resource?.type === 'document' && (resource?.fileUrl || resource?.resourceUrl)) {
            const url = resource.fileUrl || resource.resourceUrl;
            const link = document.createElement('a');
            link.href = url;
            link.download = resource.fileName || resource.title || 'download';
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const goToPrevPage = () => {
        setPageNumber(prev => Math.max(1, prev - 1));
    };

    const goToNextPage = () => {
        setPageNumber(prev => Math.min(numPages, prev + 1));
    };

    const zoomIn = () => {
        setScale(prev => Math.min(3.0, prev + 0.2));
    };

    const zoomOut = () => {
        setScale(prev => Math.max(0.5, prev - 0.2));
    };

    const isPDF = resource?.fileName?.toLowerCase().endsWith('.pdf') ||
        (resource?.fileUrl || resource?.resourceUrl)?.toLowerCase().includes('.pdf') ||
        resource?.type === 'document';

    return (
        <div className="space-y-3 sm:space-y-4">
            {/* Header with controls */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between bg-white rounded-lg border shadow-sm p-3 sm:p-4 gap-3 sm:gap-4">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm sm:text-base flex-shrink-0"
                    >
                        <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">Back to Resources</span>
                        <span className="sm:hidden">Back</span>
                    </button>

                    <div className="border-l border-gray-300 pl-3 sm:pl-4 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{resource.title}</h3>
                        <p className="text-xs sm:text-sm text-gray-500 truncate">by {resource.instructor}</p>
                    </div>
                </div>

                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    {isPDF && numPages && (
                        <>
                            {/* Zoom controls */}
                            <div className="flex items-center gap-1 border border-gray-300 rounded-lg flex-shrink-0">
                                <button
                                    onClick={zoomOut}
                                    className="p-1 sm:p-2 hover:bg-gray-100 transition-colors"
                                    disabled={scale <= 0.5}
                                >
                                    <ZoomOut className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                                <span className="px-1 sm:px-2 text-xs sm:text-sm font-medium min-w-[50px] sm:min-w-[60px] text-center">
                                    {Math.round(scale * 100)}%
                                </span>
                                <button
                                    onClick={zoomIn}
                                    className="p-1 sm:p-2 hover:bg-gray-100 transition-colors"
                                    disabled={scale >= 3.0}
                                >
                                    <ZoomIn className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                            </div>

                            {/* Page navigation */}
                            <div className="flex items-center gap-1 border border-gray-300 rounded-lg flex-shrink-0">
                                <button
                                    onClick={goToPrevPage}
                                    className="p-1 sm:p-2 hover:bg-gray-100 transition-colors"
                                    disabled={pageNumber <= 1}
                                >
                                    <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                                <span className="px-2 sm:px-3 text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-[80px] text-center">
                                    {pageNumber} of {numPages}
                                </span>
                                <button
                                    onClick={goToNextPage}
                                    className="p-1 sm:p-2 hover:bg-gray-100 transition-colors"
                                    disabled={pageNumber >= numPages}
                                >
                                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                                </button>
                            </div>
                        </>
                    )}

                    {/* Action buttons */}
                    {resource.type === 'document' && resourceUrl && (
                        <button
                            onClick={handleDownloadFile}
                            className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 transition-colors text-sm sm:text-base flex-shrink-0"
                        >
                            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Download</span>
                            <span className="sm:hidden">DL</span>
                        </button>
                    )}

                    {resource.type === 'link' && resourceUrl && (
                        <a
                            href={resourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm sm:text-base flex-shrink-0"
                        >
                            <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                            <span className="hidden sm:inline">Open Original</span>
                            <span className="sm:hidden">Open</span>
                        </a>
                    )}
                </div>
            </div>

            {/* Content viewer */}
            <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="p-2 sm:p-4">
                    {loading && (
                        <div className="flex items-center justify-center h-64 sm:h-96">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-custom-blue-1 mx-auto mb-3 sm:mb-4"></div>
                                <p className="text-gray-500 text-sm sm:text-base">Loading document...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="flex items-center justify-center h-64 sm:h-96">
                            <div className="text-center px-4">
                                <p className="text-red-500 mb-3 sm:mb-4 text-sm sm:text-base">{error}</p>
                                <p className="text-gray-500 text-sm sm:text-base">
                                    {resource.type === 'link' ? (
                                        <>This content cannot be displayed inline. </>
                                    ) : (
                                        <>Unable to load this file format. </>
                                    )}
                                    <a
                                        href={resourceUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-custom-blue-1 hover:underline"
                                    >
                                        Open in new tab
                                    </a>
                                </p>
                            </div>
                        </div>
                    )}

                    {/* PDF Viewer */}
                    {isPDF && resourceUrl && (
                        <div className="flex justify-center overflow-auto">
                            <Document
                                file={resourceUrl}
                                onLoadSuccess={onDocumentLoadSuccess}
                                onLoadError={onDocumentLoadError}
                                loading={null}
                                error={null}
                            >
                                <Page
                                    pageNumber={pageNumber}
                                    scale={scale}
                                    renderAnnotationLayer={true}
                                    renderTextLayer={true}
                                    className="max-w-full"
                                />
                            </Document>
                        </div>
                    )}

                    {/* Iframe fallback for non-PDF content */}
                    {!isPDF && !error && !loading && resourceUrl && (
                        <div className="w-full h-[50vh] sm:h-[70vh] border rounded-lg overflow-hidden">
                            <iframe
                                src={resourceUrl}
                                className="w-full h-full border-0"
                                title={resource.title}
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                                onLoad={() => setLoading(false)}
                                onError={() => {
                                    setError('Failed to load content');
                                    setLoading(false);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ResourceViewer;
